import { useMutation, useQueryClient } from "react-query";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ObjectTagValuesConfigurations } from "../../services/object-tag-values-data-service/ConfigurationModels";
import ObjectTagValuesDataService from "../../services/object-tag-values-data-service/ObjectTagValuesDataService";
import { RestError } from "../../services/rest-service/RestService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { MutationContextType } from "./Common";
import { keys } from "./Keys";

export type UseDeleteObjectTagValuesConfigurationResultType = {
    deletedName: string;
};

export type UseDeleteObjectTagValuesConfigurationParameters = {
    groupID: number,
    objectType: eLynxObjectTypeEnum,
    objectID: number,
    configurationID: number
} & BaseUseMutationParameters<UseDeleteObjectTagValuesConfigurationResultType, void>;

export function useDeleteObjectTagValuesConfiguration({
    groupID,
    objectType,
    objectID,
    configurationID,
    onError,
    onSuccess
}: UseDeleteObjectTagValuesConfigurationParameters) {
    const queryClient = useQueryClient();

    return useMutation<UseDeleteObjectTagValuesConfigurationResultType, RestError, void, MutationContextType>(
        async () => {

            const model = await ObjectTagValuesDataService.getConfiguration(groupID, configurationID);

            await ObjectTagValuesDataService.deleteConfiguration(
                groupID,
                model
            );

            return {
                deletedName: model.name ?? ""
            };
        },
        {
            onMutate: async () => {

                await queryClient.cancelQueries(keys.all);

                const previousConfigurations = queryClient.getQueryData<ObjectTagValuesConfigurations>(
                    keys.configurationList(
                        groupID,
                        objectType,
                        objectID
                    )
                );

                queryClient.setQueryData<ObjectTagValuesConfigurations>(
                    keys.configurationList(
                        groupID,
                        objectType,
                        objectID
                    ),
                    (old?: ObjectTagValuesConfigurations) => {

                        const c = old as ObjectTagValuesConfigurations;
                        c.configurations = c.configurations?.filter(o => o.id !== configurationID);
                        return c;
                    }
                );

                return {
                    previousConfigurations: previousConfigurations
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _runTicket, context) => {

                if (error && context?.previousConfigurations) {
                    queryClient.setQueryData<ObjectTagValuesConfigurations>(
                        keys.configurationList(
                            groupID,
                            objectType,
                            objectID
                        ),
                        context?.previousConfigurations
                    );
                }

                queryClient.invalidateQueries(keys.configurationLists());
            }
        }
    );
}